<template>
	<div class="container__model__preview">
		<div class="upper_preview">
			<h1 class="model__name">
				{{ model.stan_nm_titulo_modelo }}
			</h1>
			<div
				v-if="graficoAmostrado"
				class="badge__amostra__wrapper"
			>
				<span
					class="badge__amostra"
					:title="tooltipAmostra"
				>
					<i class="fas fa-info-circle"></i>
					Amostra de {{ model.graficosModelo.pontosAmostrados.toLocaleString() }} de
					{{ model.graficosModelo.pontosTotais.toLocaleString() }} pontos
				</span>
			</div>
			<div
				v-else-if="model.graficosModelo && model.graficosModelo.grande"
				class="badge__amostra__wrapper"
			>
				<span
					class="badge__amostra"
					title="Os gráficos são renderizados no servidor (imagem) com TODOS os pontos."
				>
					<i class="fas fa-info-circle"></i>
					Gráficos com todos os {{ model.graficosModelo.pontosTotais.toLocaleString() }} pontos
					(imagem)
				</span>
			</div>
			<div class="container__model_kpis">
				<KPI
					:value="
						model.stan_md_erro_porcentagem != null ? model.stan_md_erro_porcentagem.toFixed(3) : '-'
					"
					:description="`% ${$t('error')}`"
				/>
				<KPI
					:value="model.stan_md_erro != null ? model.stan_md_erro.toFixed(3) : '-'"
					:description="$t('error')"
				/>
				<KPI
					:value="
						model.stan_md_coeficiente_assimetria != null
							? model.stan_md_coeficiente_assimetria.toFixed(3)
							: '-'
					"
					:description="$t('asymmetry')"
				/>
				<KPI
					:value="
						model.stan_md_coeficiente_curtose != null
							? model.stan_md_coeficiente_curtose.toFixed(3)
							: '-'
					"
					:description="$t('kurtosis')"
				/>
			</div>
		</div>
		<div class="lower_preview">
			<!-- Dataset grande (>10k): o worker já renderizou as imagens (R/ggplot2) no
			     processamento; aqui só exibimos as URLs persistidas num carrossel. -->
			<div
				v-if="model.graficosModelo && model.graficosModelo.grande"
				class="container__image"
			>
				<v-carousel
					v-if="imagensCarrossel.length"
					height="100%"
					hide-delimiter-background
					show-arrows="always"
					:continuous="false"
					class="graficos__carrossel"
				>
					<v-carousel-item
						v-for="(img, i) in imagensCarrossel"
						:key="i"
					>
						<div class="graficos__carrossel__slide">
							<img
								:src="img.src"
								class="grafico__img"
								:alt="img.titulo"
							/>
						</div>
					</v-carousel-item>
				</v-carousel>
				<div
					v-else
					class="graficos__img__estado"
				>
					Gráficos não disponíveis para este modelo.
				</div>
			</div>
			<!-- Dataset pequeno/legado: gráficos interativos. Só quando graficosModelo
			     existe — senão o modelo veio sem gráficos e o acesso quebraria. -->
			<div
				v-else-if="model.graficosModelo"
				class="container__image"
			>
				<RealEstimado
					:info-grafico="tipoInfoGrafico"
					class="main__parcels__chart"
					:series="model.graficosModelo.realEstimado"
				/>
				<Residuos
					:info-grafico="tipoInfoGrafico"
					class="main__parcels__chart"
					:series="model.graficosModelo.residuo"
				/>
				<Histogramas
					:info-grafico="tipoInfoGrafico"
					:y-titulo="$t('quantity')"
					:x-titulo="`Resíduos ${tipoInfoGrafico.unidade}`"
					:titulo="$t('residueHistogram')"
					class="main__parcels__chart"
					:series="(model.graficosModelo.histograma || []).map((n) => n.x)"
				/>
			</div>
			<div
				v-else
				class="container__image graficos__img__estado"
			>
				Gráficos não disponíveis para este modelo.
			</div>
		</div>
		<slot />
	</div>
</template>

<script>
import KPI from '../KPI/KPI'
import Histogramas from '../Graficos/Histogramas'
import RealEstimado from '../Graficos/RealEstimado'
import Residuos from '../Graficos/Residuos'

export default {
	components: { KPI, Residuos, RealEstimado, Histogramas },
	props: {
		model: {
			type: Object,
			required: true,
		},

		tipoInfoGrafico: {
			type: Object,
			required: true,
		},
	},
	computed: {
		// Imagens (URLs persistidas pelo worker) na ordem do carrossel (dataset grande).
		imagensCarrossel() {
			const g = this.model?.graficosModelo
			if (!g) return []
			const itens = []
			if (g.realEstimadoImgUrl) itens.push({ src: g.realEstimadoImgUrl, titulo: 'Real × Estimado' })
			if (g.residuoImgUrl) itens.push({ src: g.residuoImgUrl, titulo: 'Resíduos' })
			if (g.histogramaImgUrl)
				itens.push({ src: g.histogramaImgUrl, titulo: 'Histograma de Resíduos' })
			return itens
		},
		graficoAmostrado() {
			const g = this.model?.graficosModelo
			return !!g && g.pontosAmostrados > 0 && g.pontosTotais > g.pontosAmostrados
		},
		tooltipAmostra() {
			return (
				'Para manter a performance, os gráficos exibem uma amostra representativa ' +
				'dos pontos (agrupada por faixa de idade, preservando os extremos de cada faixa). ' +
				'O modelo foi ajustado sobre TODOS os pontos — a amostra afeta apenas a visualização.'
			)
		},
	},
}
</script>

<style scoped>
.badge__amostra__wrapper {
	display: flex;
	justify-content: center;
	margin-top: -28px;
	margin-bottom: 20px;
}

.badge__amostra {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 2px 10px;
	border-radius: 10px;
	background-color: #fff4e5;
	color: #8a5a00;
	border: 1px solid #ffd599;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	cursor: help;
}

.container__model_kpis {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
}

.model__name,
.model__modelo {
	color: #081f44;
	font-size: 26px;
	padding-bottom: 40px;
	text-align: center;
}

.container__model__preview {
	height: 90%;
	display: flex;
	flex-direction: column;
	/* justify-content: space-between; */
	/* max-height: 60vh;
  margin-bottom: 10rem; */
}

.container__image {
	display: flex;
	flex-direction: row;
	height: 100%;
	/* height: 100%; */
}

.grafico__img {
	max-width: 100%;
	max-height: 100%;
	width: auto;
	height: auto;
	object-fit: contain;
	align-self: center;
}

.graficos__carrossel {
	width: 100%;
}

.graficos__carrossel__slide {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 8px 40px 32px;
	box-sizing: border-box;
}

.graficos__img__estado {
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: center;
	color: rgb(149, 187, 73);
	font-weight: 600;
}

.lower_preview {
	height: 50vh;
}

@media screen and (min-device-width: 1200px) and (max-device-width: 1600px) and (-webkit-min-device-pixel-ratio: 1) {
	.container__model__preview {
		justify-content: none !important;
	}
}

@media screen and (max-width: 1370px) {
	.model__name,
	.model__modelo {
		color: #081f44;
		font-size: 18px;
		padding-bottom: 5px;
		padding-top: 5px;
		text-align: center;
	}
	.container__model_kpis {
		font-size: 0.2rem !important;
	}
}
img {
	height: 18rem;
	margin: 0 auto;
}
.container__cardgrafico.main__parcels__chart {
	height: 100%;
}
.cor {
	color: black !important;
}
</style>
